<template>
  <div class="mt-4">
    <div class="heading-1">Log in</div>
    <p class="text-3.4 font-400">Continue to Seller Dashboard</p>
  </div>

  <div class="mt-8">
    <v-dynamic-form
      v-model="credentials"
      v-model:valid="valid"
      :inputs="inputs"
      @keyup.enter="submit"
    />

    <v-alert
      v-if="error"
      class="my-2"
      border="end"
      type="error"
      density="compact"
      variant="tonal"
      rounded="lg"
      colored-border
    >
      Invalid username or password
    </v-alert>

    <v-btn
      :loading="loading"
      :disabled="!valid"
      color="primary"
      rounded="lg"
      height="38"
      block
      @click="handleLogin"
    >
      Log in
    </v-btn>

    <div class="my-5 px-8">
      <v-divider>or</v-divider>
    </div>

    <div class="grid grid-cols-3 gap-2">
      <v-card
        v-for="value in socialLogins"
        @click="socialLogin(value.provider)"
        class="bg-gray-100"
        :key="value.provider"
        rounded="lg"
        flat
      >
        <div :title="value.name" class="p-2 flex-center">
          <Icon :name="value.icon" size="24" />
        </div>
      </v-card>
    </div>

    <div class="mt-8 flex items-center gap-2">
      <span class="caption">New to Orie?</span>
      <v-btn class="normal-case" :to="{ name: 'register' }">
        Create an account
        <!--  -->
      </v-btn>
    </div>

    <router-link
      :to="{ name: 'oauth-forgot-password' }"
      class="mt-2 link text-3"
    >
      Forgot password
    </router-link>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { PasswordInputField } from "#components";
import { Rule } from "~/services/Rules";
import type { LoginInput } from "~/layers/admin/modules/auth/runtime/types/api";

export default defineComponent({
  setup() {
    definePageMeta({
      layout: "auth",
      auth: false,
      middleware: "redirect-login",
    });

    const valid = ref(false);

    const credentials = ref<LoginInput>({
      username: "",
      password: "",
    });

    const { loading, error, socialLogins, socialLogin, login, onEvent } =
      useSellerAuth();

    const inputs = computed(() => {
      return defineFormInputs({
        username: {
          hideName: true,
          name: "Email",
          type: "text",
          rules: Rule.required(),
          props: {
            label: "Email",
            density: "compact",
            variant: "outlined",
            rounded: "lg",
          },
        },
        password: {
          hideName: true,
          name: "Password",
          rules: Rule.required(),
          component: PasswordInputField,
          props: {
            label: "Password",
            density: "compact",
            variant: "outlined",
            rounded: "lg",
            type: "password",
          },
        },
      });
    });

    onEvent("login:new-user", () => {
      navigateTo({ name: "onboarding-new-store" });
    });

    const submit = () => {
      if (valid.value) {
        login(credentials.value);
      }
    };

    const handleLogin = () => {
      login(credentials.value);
    };

    onMounted(() => {
      const route = useRoute();
      if (route.query.email) {
        credentials.value.username = route.query.email as string;
      }
    });

    return {
      credentials,
      inputs,
      socialLogins,
      handleLogin,
      loading,
      error,
      valid,
      submit,
      socialLogin,
    };
  },
});
</script>
